import { Capacitor } from '@capacitor/core';

/**
 * Resize longest edge to `maxEdge` and encode as JPEG (browser canvas).
 */
export async function compressImageToJpeg(blob, maxEdge = 1600, quality = 0.85) {
  const fromBitmap = async () => {
    const bitmap = await createImageBitmap(blob);
    try {
      let { width, height } = bitmap;
      const max = Math.max(width, height);
      const scale = max > maxEdge ? maxEdge / max : 1;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas unsupported');
      ctx.drawImage(bitmap, 0, 0, w, h);
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('JPEG encode failed'))),
          'image/jpeg',
          quality,
        );
      });
    } finally {
      try {
        bitmap.close();
      } catch {
        /* ignore */
      }
    }
  };

  if (typeof createImageBitmap === 'function') {
    try {
      return await fromBitmap();
    } catch {
      /* fall through */
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const max = Math.max(width, height);
      const scale = max > maxEdge ? maxEdge / max : 1;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas unsupported'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('JPEG encode failed'))),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };
    img.src = url;
  });
}

/** Native: open photo library via Capacitor Camera (returns original blob; compress separately). */
export async function pickPhotoBlobFromNativeLibrary() {
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  const photo = await Camera.getPhoto({
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Photos,
  });
  const webPath = photo.webPath;
  if (!webPath) throw new Error('No image path from camera');
  const res = await fetch(webPath);
  if (!res.ok) throw new Error('Could not read photo');
  return res.blob();
}

export function isNativePhotoPicker() {
  try {
    return Boolean(Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
}
